import {
    isRunningInSEBBrowser,
    getUserToken,
} from '@amplify/assessment-common/secure-exam-browser';
import { datadogRum } from '@datadog/browser-rum';
import { dataDogAddWarning } from '../logging/datadog';
import { isEmpty } from 'lodash';
import AQUAServiceClient from '../clients/AQUAServiceClient';
import { STUDENT_ASSESSMENT, PORTAL_DASHBOARD } from '../../constants/url';
import keycloak, { keycloakInitOptions } from '../keycloak/keycloak';
import { isMigrationFailed } from '../keycloak/keycloakUtils';
import { broadcastLogout } from '../keycloak/logoutBroadcast';
import USER_TYPE from '../../constants/userType';
import selectors from '../../rootSelectors';
import { SEB_AUTH_URL } from '../../constants/secureBrowser';
import {
    isChromeOS,
    isCompanionExtensionAvailable,
    isKioskChromeApp,
    removeKioskModeParameters,
    setLouisianaCookie,
    delayKioskLaunch,
} from '../utils/browserUtils';
import { clearState } from '../../AppStateActions';

export const SET_USER = 'SET_USER';

export const SET_USER_FROM_STORAGE = 'SET_USER_FROM_STORAGE';
export const AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED';

export const setUser = (userId, userName, userType, aquaId) => ({
    type: SET_USER,
    userId,
    userName,
    userType,
    aquaId,
});

export const authenticationFailed = error => ({
    type: AUTHENTICATION_FAILED,
    errorMessage: error.message,
});

const getKeycloakInitOptions = async () => {
    if (isRunningInSEBBrowser()) {
        setLouisianaCookie();
        try {
            if (isEmpty(keycloak.tokenParsed)) {
                const { keycloak_token, refresh_token } = await getUserToken(SEB_AUTH_URL);
                return {
                    ...keycloakInitOptions,
                    onLoad: 'check-sso',
                    token: keycloak_token,
                    refreshToken: refresh_token,
                };
            }
        } catch (error) {
            // Unable to get user tokens from SEB server
            dataDogAddWarning('Unable to get user tokens from SEB server', { error });
            // eslint-disable-next-line no-console
            console.warn('Unable to get user tokens from SEB server', error);
            return keycloakInitOptions;
        }
    }
    return keycloakInitOptions;
};

export const initializeKeycloak = () => async dispatch => {
    // didInitialize guards against a second call e.g. when AuthProvider remounts
    // under StrictMode in dev or if the provider is ever rendered twice. keycloak-js
    // v26 throws hard on double init.
    if (keycloak.didInitialize) return undefined;
    try {
        const initOptions = await getKeycloakInitOptions();
        const authenticated = await keycloak.init(initOptions);
        if (authenticated) return undefined;
        if (isChromeOS() && isKioskChromeApp()) {
            setLouisianaCookie(); // keep backward compatible for chrome apps until retired
        }
        if (isChromeOS() && (await isCompanionExtensionAvailable())) {
            setLouisianaCookie();
            // Delay login redirect to let ChromeOS pull and process manifest icons
            await delayKioskLaunch();
        }
        return keycloak.login();
    } catch (error) {
        dispatch(authenticationFailed(error));
        throw error;
    }
};

export const resolveUser = () => (dispatch, getStore) => {
    const state = getStore();
    const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(state);

    return Promise.resolve()
        .then(() => {
            if (keycloak.tokenParsed !== undefined) {
                if (isMigrationFailed()) {
                    // TODO: Add proper messaging
                    const migrationError = new Error('There is a problem with your account setup.');
                    dispatch(authenticationFailed(migrationError));
                    return Promise.reject(migrationError);
                }
            }
            return Promise.resolve();
        })
        .then(() => {
            const userType = keycloak.tokenParsed.realm_access.roles.includes(USER_TYPE.STUDENT)
                ? USER_TYPE.STUDENT
                : '';
            if (!isExternalURLSourcePresent) {
                const userId = keycloak.tokenParsed.ampId;
                const persistedUserId = selectors.getPersistedUserId(state);
                if (persistedUserId !== undefined && persistedUserId !== userId) {
                    // clear state if user has changed; having different users at this point means that page was reloaded
                    // on non-landing URL, and redux state was loaded from session storage, along data of previous user
                    dispatch(clearState());
                }
                return dispatch(setUser(userId, keycloak.tokenParsed.name, userType));
            }
            return dispatch(AQUAServiceClient.getAQUAUserId()).then(response => {
                if (response) {
                    return dispatch(AQUAServiceClient.getAQUAUserDataById(response.UserId)).then(
                        userData => {
                            if (userData) {
                                return dispatch(
                                    setUser(
                                        userData.UserExternalId,
                                        keycloak.tokenParsed.name,
                                        userType,
                                        userData.UserId,
                                    ),
                                );
                            }
                            return Promise.reject(userData);
                        },
                    );
                }
                return Promise.reject(response);
            });
        })
        .catch(error => {
            dispatch(authenticationFailed(error));
            return Promise.reject(error);
        });
};

export const logUserOut = () => {
    const keycloakLogoutOptions = {
        redirectUri: `${window.location.origin}${STUDENT_ASSESSMENT}`,
    };
    removeKioskModeParameters();
    if (datadogRum) {
        datadogRum.stopSessionReplayRecording();
    }
    // Tell other tabs of this app to log out too.
    broadcastLogout();
    return keycloak.logout(keycloakLogoutOptions);
};

export const handleAuthenticationFailed = () => {
    logUserOut();
};

export const portalRedirect = () => {
    window.location = PORTAL_DASHBOARD;
};
